import React, { useEffect } from 'react';
import SatInput from '@components/common/satUI/SatInput';
import SatModal from '@components/common/satUI/SatModal';
import SatCheckBox from '@components/common/satUI/SatCheckBox';
import SatValidationErrMsg from '@components/common/satUI/SatValidationErrMsg';
import { isEmptyString } from '@common/utils';

const InputRefundInterest = ({
  type,
  refund,
  interest,
  refundKey,
  interestKey,
  refundInput, // 'input' / 'select'
  interestInput, // 'input' / 'select'
  isErrorVisible,
  onChange,
  onChangeOption,
}) => {
  const {
    isOn: isShowModal,
    on: showModal,
    off: hideModal,
  } = $hooks.useToggler();

  const typeKey = type === 'refund' ? refundKey : interestKey;
  const inputValue = type === 'refund' ? refund : interest;
  const isRefundInput = refundInput === 'input';
  const isInterestInput = interestInput === 'input';
  const refundOptions = [
    '1천만원이하',
    '5천만원이하',
    '1억원이하',
    '1억원초과',
  ];
  const interestOptions = ['3% 이하', '5% 이하', '5% 초과'];

  const { error: toastError } = $hooks.useToast();

  const initData = key => {
    if (key === refundKey) {
      const isRefundInput =
        !$utils.isEmptyString(refund) && !refundOptions.includes(refund);
      const newRefundInput = isRefundInput ? 'input' : 'select';
      onChangeOption('refundInput', newRefundInput);
    } else if (key === interestKey) {
      const isNewInterestInput =
        !$utils.isEmptyString(interest) && !interestOptions.includes(interest);
      const newInterestInput = isNewInterestInput ? 'input' : 'select';
      onChangeOption('interestInput', newInterestInput);
    }
  };

  const decorateValue = (key, value) => {
    if (key === refundKey) return `${$utils.formatPrice(value)}원`;
    else if (key === interestKey) return `${value}%`;
  };

  const handleChange = (key, value) => {
    if (key === interestKey) {
      const isOver100 = value > 100;
      if (isOver100) return toastError('100% 를 초과할 수 없습니다.');
    }

    const decoratedValue = decorateValue(key, value);
    onChange(key, decoratedValue);
  };

  const handleDelete = () => {
    if (type === 'refund') onChange(refundKey, '');
    else if (type === 'interest') onChange(interestKey, '');
  };

  const handleSelectOption = (type, option) => {
    if (type === 'refund') {
      onChangeOption('refundInput', option);
      onChange(refundKey, '');
    } else if (type === 'interest') {
      onChangeOption('interestInput', option);
      onChange(interestKey, '');
    }
  };

  useEffect(() => initData(typeKey), []);

  return (
    <>
      <SatInput
        noFocus
        value={inputValue}
        onClick={showModal}
        onDelete={handleDelete}
      />
      <SatValidationErrMsg
        type="error"
        visible={isErrorVisible && isEmptyString(inputValue)}
        message="값을 입력해주세요."
      />

      <SatModal
        isPortal
        wide
        type="info"
        visible={isShowModal}
        setVisible={hideModal}
      >
        <header className="modal-header">
          <h3 className="layer-tit2">해약환급금/예정이자율 중 공시이율</h3>
        </header>

        <button
          type="button"
          className="btn-layer-close modal-close"
          onClick={hideModal}
        >
          닫기
        </button>

        <div className="cont">
          <div className="insu-box">
            <label>클릭 후 해약환급금 선택 또는 직접기재</label>

            <table className="tbl-sty5 mgt-16">
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: 'auto' }} />
              </colgroup>

              <tbody>
                <tr>
                  <td>
                    <SatCheckBox
                      value={[!isRefundInput]}
                      options={[{ label: '택1', value: true }]}
                      onChange={() => handleSelectOption('refund', 'select')}
                    />
                  </td>

                  <td>
                    <div className="check-box mg-0">
                      {refundOptions.map((refundOption, index) => (
                        <div
                          key={refundOption}
                          className="check-wrap chk-style"
                        >
                          <input
                            id={`refund-${index}`}
                            type="radio"
                            name="refund"
                            disabled={isRefundInput}
                            checked={refundOption === refund}
                            onChange={() => onChange(refundKey, refundOption)}
                          />
                          <label htmlFor={`refund-${index}`}>
                            {refundOption}
                          </label>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <SatCheckBox
                      value={[isRefundInput]}
                      options={[{ label: '직접기재', value: true }]}
                      onChange={() => handleSelectOption('refund', 'input')}
                    />
                  </td>

                  <td>
                    <SatInput
                      type="currency"
                      currencyType="W"
                      textAlign="right"
                      disabled={!isRefundInput}
                      value={isRefundInput ? $utils.cleanNumber(refund) : ''}
                      displayValue={isRefundInput ? refund : null}
                      onChange={value => handleChange(refundKey, value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="insu-box mgt-20">
            <label>클릭 후 예정이자율 중 공시이율 선택 또는 직접기재</label>

            <table className="tbl-sty5 mgt-16">
              <colgroup>
                <col style={{ width: '20%' }} />
                <col style={{ width: 'auto' }} />
              </colgroup>

              <tbody>
                <tr>
                  <td>
                    <SatCheckBox
                      value={[!isInterestInput]}
                      options={[{ label: '택1', value: true }]}
                      onChange={() => handleSelectOption('interest', 'select')}
                    />
                  </td>

                  <td>
                    <div className="check-box mg-0">
                      {interestOptions.map((interestOption, index) => (
                        <div
                          key={interestOption}
                          className="check-wrap chk-style"
                        >
                          <input
                            id={`interest-${index}`}
                            type="radio"
                            name="interest"
                            disabled={isInterestInput}
                            checked={interestOption === interest}
                            onChange={() =>
                              onChange(interestKey, interestOption)
                            }
                          />
                          <label htmlFor={`interest-${index}`}>
                            {interestOption}
                          </label>
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <SatCheckBox
                      value={[isInterestInput]}
                      options={[{ label: '직접기재', value: true }]}
                      onChange={() => handleSelectOption('interest', 'input')}
                    />
                  </td>

                  <td>
                    <SatInput
                      useDot
                      type="percent"
                      textAlign="right"
                      decimalPoint={2}
                      maxValue={100}
                      disabled={!isInterestInput}
                      value={
                        isInterestInput ? $utils.cleanNumber(interest) : ''
                      }
                      displayValue={isInterestInput ? interest : null}
                      onChange={value => handleChange(interestKey, value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="layer-footer">
          <button
            type="button"
            className="btn-s blue btn-wide modal-close"
            onClick={hideModal}
          >
            저장
          </button>
        </div>
      </SatModal>
    </>
  );
};

export default InputRefundInterest;
